import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from '@emotion/styled';
import { parseXMLFile } from './utils/xmlParser';
// import { generateNewXML } from './utils/xmlGenerator'; // Nieużywane
import { updateProductPriorities, updateIdoSellApiKey, API_KEY, getKMXApiBase } from './utils/shopApi';
import ProductGrid from './components/ProductGrid';
import MenuSelector from './components/MenuSelector';
// import LoadingSpinner from './components/LoadingSpinner'; // Nieużywane - zastąpione modalem
import PrioritySnapshotsManager from './components/PrioritySnapshotsManager';
import { saveCurrentPrioritiesSnapshot } from './utils/snapshotManager';
import './App.css';

const AppContainer = styled.div`
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 30px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const SidebarContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-height: 400px;
  background: #ffffff;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background-color: #0EB66E;
  }
`;

const InitialMessage = styled.div`
  color: #666;
  padding: 40px 20px;
  width: 100%;
  font-size: 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  box-sizing: border-box;
`;

const LoadOptionsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  width: 100%;
  max-width: 800px;
  margin: 24px auto 0 auto;
  box-sizing: border-box;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const LoadOption = styled.div`
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: all 0.2s ease;

  &:hover {
    border-color: #0EB66E;
    box-shadow: 0 2px 12px rgba(14, 182, 110, 0.1);
  }
`;

const OptionTitle = styled.h3`
  margin: 0 0 12px 0;
  color: #333;
  font-size: 18px;
  font-weight: 600;
`;

const OptionDescription = styled.p`
  margin: 0 0 24px 0;
  color: #888;
  font-size: 13px;
  line-height: 1.5;
`;

const ActionButton = styled.button`
  padding: 14px 20px;
  background-color: ${props => props.variant === 'primary' ? '#0EB66E' : '#333333'};
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  width: ${props => props.fullWidth ? '100%' : 'auto'};
  min-width: 200px;
  margin-top: 20px;
  transition: all 0.3s ease;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.3px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    background-color: ${props => props.variant === 'primary' ? '#0ca362' : '#000000'};
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(14, 182, 110, 0.2);
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const FileInput = styled.input`
  display: none;
`;

// Funkcja do odczytu pliku jako tekst
const readFileAsText = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (e) => reject(new Error('Błąd odczytu pliku'));
    reader.readAsText(file);
  });
};

function App() {
  const [products, setProducts] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [modifiedProducts, setModifiedProducts] = useState([]);
  const [modifiedNodes, setModifiedNodes] = useState(new Set());
  const [savedNodes, setSavedNodes] = useState(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const [isRefreshingVisibility, setIsRefreshingVisibility] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState({ current: 0, total: 0, percent: 0 });
  const [refreshMessage, setRefreshMessage] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [downloadProgress, setDownloadProgress] = useState({ loaded: 0, total: 0, percent: 0 });
  const [preloadedXML, setPreloadedXML] = useState(null);
  const [offerGeneratedDate, setOfferGeneratedDate] = useState(null);
  const [isPreloading, setIsPreloading] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settingsApiKey, setSettingsApiKey] = useState('');
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  // Funkcja wyświetlająca komunikaty dla użytkownika
  const showMessage = (text, type = 'info') => {
    setMessage(text);
    setMessageType(type);
    
    // Automatyczne ukrywanie komunikatu po 5 sekundach
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000);
  };

  // Funkcja do obliczania czasu do następnej aktualizacji oferty
  // Liczy 4 godziny od rzeczywistej daty modyfikacji pliku
  const calculateTimeToNextUpdate = (lastGeneratedDate = null) => {
    const now = new Date();
    
    if (lastGeneratedDate) {
      // Użyj rzeczywistej daty modyfikacji pliku + 4 godziny
      const lastGenerated = new Date(lastGeneratedDate);
      const nextUpdate = new Date(lastGenerated.getTime() + (4 * 60 * 60 * 1000)); // +4 godziny
      
      // Jeśli następna aktualizacja już minęła, dodaj kolejne 4 godziny
      while (nextUpdate <= now) {
        nextUpdate.setTime(nextUpdate.getTime() + (4 * 60 * 60 * 1000));
      }
      
      const diff = nextUpdate - now;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      
      return { hours, minutes, nextUpdateTime: nextUpdate };
    } else {
      // Fallback do starej logiki (sztywny harmonogram co 4 godziny)
      const updateHours = [3, 7, 11, 15, 19, 23];
      
      // Znajdź najbliższą godzinę aktualizacji
      let nextUpdateHour = updateHours.find(hour => hour > now.getHours());
      
      // Jeśli nie ma dziś, weź pierwszą jutro
      if (!nextUpdateHour) {
        nextUpdateHour = updateHours[0];
      }
      
      const nextUpdate = new Date(now);
      if (nextUpdateHour < now.getHours() || (nextUpdateHour === now.getHours() && now.getMinutes() > 0)) {
        // Jutro
        nextUpdate.setDate(nextUpdate.getDate() + 1);
      }
      nextUpdate.setHours(nextUpdateHour, 0, 0, 0);
      
      const diff = nextUpdate - now;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      
      return { hours, minutes, nextUpdateTime: nextUpdate };
    }
  };

  // Stan dla licznika aktualizacji
  const [timeToUpdate, setTimeToUpdate] = useState(calculateTimeToNextUpdate());
  
  // Aktualizuj licznik co minutę
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeToUpdate(calculateTimeToNextUpdate(offerGeneratedDate));
    }, 60000); // Co minutę
    
    return () => clearInterval(interval);
  }, [offerGeneratedDate]);

  // Aktualizuj licznik gdy zmieni się data generowania pliku
  useEffect(() => {
    if (offerGeneratedDate) {
      setTimeToUpdate(calculateTimeToNextUpdate(offerGeneratedDate));
    }
  }, [offerGeneratedDate]);

  // Funkcja do pobierania daty modyfikacji pliku z nagłówków HTTP
  const getFileLastModified = async () => {
    const API_ENDPOINT = `${getKMXApiBase()}/api/xml/fetch`;
    try {
      const response = await fetch(API_ENDPOINT, {
        method: 'HEAD',
        headers: {
          'X-API-KEY': API_KEY,
          'Accept': 'application/xml, text/xml'
        }
      });
      
      if (!response.ok) {
        const body = await response.text().catch(() => '');
        console.error('[Diagnostyka] HEAD', API_ENDPOINT, 'status=', response.status, 'X-API-KEY length=', API_KEY?.length, 'body=', body);
        throw new Error(`Błąd sprawdzania: ${response.status}${body ? ' - ' + body : ''}`);
      }
      
      const lastModified = response.headers.get('Last-Modified');
      if (lastModified) {
        // Konwertuj GMT na format YYYY-MM-DD HH:MM:SS (lokalna strefa czasowa)
        const date = new Date(lastModified);
        
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      }
      
      return null;
    } catch (error) {
      console.error('Błąd podczas pobierania daty modyfikacji:', error);
      return null;
    }
  };

  // Diagnostyka: sprawdź długość klucza na serwerze (do porównania z frontendem)
  const checkDiagnostic = useCallback(async () => {
    try {
      const r = await fetch(`${getKMXApiBase()}/api/diagnostic`);
      const j = await r.json();
      console.log('[Diagnostyka] Serwer oczekuje klucza o długości:', j.authKeyLength, 'źródło:', j.source, '| Frontend wysyła klucz o długości:', API_KEY?.length);
    } catch (e) {
      console.error('[Diagnostyka] Błąd:', e);
    }
  }, []);

  // Funkcja do preloadowania XML (pobieranie bez parsowania)
  const preloadXMLFromServer = useCallback(async () => {
    const API_ENDPOINT = `${getKMXApiBase()}/api/xml/fetch`;
    try {
      setIsPreloading(true);
      setDownloadProgress({ loaded: 0, total: 0, percent: 0 });
      
      console.log('Preloadowanie XML z serwera:', API_ENDPOINT);
      await checkDiagnostic();
      
      // Najpierw pobierz datę modyfikacji z nagłówków HTTP
      const lastModifiedDate = await getFileLastModified();
      console.log('Data modyfikacji pliku (HTTP):', lastModifiedDate);
      
      const response = await fetch(API_ENDPOINT, {
        method: 'GET',
        headers: {
          'X-API-KEY': API_KEY,
          'Accept': 'application/xml, text/xml'
        }
      });
      
      if (!response.ok) {
        const body = await response.text().catch(() => '');
        console.error('[Diagnostyka] GET', API_ENDPOINT, 'status=', response.status, 'X-API-KEY length=', API_KEY?.length, 'body=', body);
        let msg = `Błąd pobierania: ${response.status}`;
        try {
          const j = JSON.parse(body);
          if (j && j.error) msg += ' - ' + j.error;
        } catch (_) {
          if (body) msg += ' - ' + body;
        }
        throw new Error(msg);
      }
      
      const contentLength = response.headers.get('Content-Length');
      const total = parseInt(contentLength, 10);
      
      const reader = response.body.getReader();
      let receivedLength = 0;
      const chunks = [];
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        chunks.push(value);
        receivedLength += value.length;
        
        const percent = total ? Math.round((receivedLength / total) * 100) : 0;
        setDownloadProgress({
          loaded: receivedLength,
          total: total,
          percent: percent
        });
      }
      
      const chunksAll = new Uint8Array(receivedLength);
      let position = 0;
      for (let chunk of chunks) {
        chunksAll.set(chunk, position);
        position += chunk.length;
      }
      
      const xmlContent = new TextDecoder("utf-8").decode(chunksAll);
      console.log(`XML preloadowany, rozmiar: ${(xmlContent.length / 1024 / 1024).toFixed(2)} MB`);
      
      // Użyj daty z nagłówka HTTP zamiast parsowania XML
      setPreloadedXML(xmlContent);
      setOfferGeneratedDate(lastModifiedDate);
      setIsPreloading(false);
      
    } catch (error) {
      console.error('Błąd podczas preloadowania XML:', error);
      setError(`Błąd podczas pobierania oferty: ${error.message}`);
      setIsPreloading(false);
    }
  }, [checkDiagnostic]);

  // Funkcja do sprawdzania czy jest nowszy plik XML (używając nagłówków HTTP)
  const checkForNewXML = useCallback(async () => {
    try {
      console.log('Sprawdzanie czy jest nowszy plik XML...');
      
      const newLastModified = await getFileLastModified();
      console.log('Nowa data modyfikacji (HTTP):', newLastModified);
      console.log('Zapisana data modyfikacji:', offerGeneratedDate);
      
      // Jeśli data się zmieniła, pobierz pełny plik
      if (newLastModified && newLastModified !== offerGeneratedDate) {
        console.log('Znaleziono nowszy plik XML! Pobieranie...');
        await preloadXMLFromServer();
      } else {
        console.log('Plik XML jest aktualny');
      }
      
    } catch (error) {
      console.error('Błąd podczas sprawdzania nowego XML:', error);
    }
  }, [offerGeneratedDate, preloadXMLFromServer]);

  // Funkcja do załadowania preloadowanego XML
  const handleLoadPreloadedXML = async () => {
    if (!preloadedXML) {
      showMessage('Brak pobranego pliku XML', 'error');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      console.log('Parsowanie preloadowanego XML...');
      const parsedData = await parseXMLFile(preloadedXML, true);
      
      if (!parsedData.products || parsedData.products.length === 0) {
        throw new Error('Nie znaleziono produktów w pliku XML');
      }
      
      console.log(`Załadowano ${parsedData.products.length} produktów`);
      
      setProducts(parsedData.products);
      setNodes(parsedData.menuNodes);
      
      // Zapisujemy początkowy stan
      const fileName = `Oferta ${offerGeneratedDate || new Date().toLocaleString('pl-PL')}`;
      saveCurrentPrioritiesSnapshot(parsedData.products, fileName);
      
      setLoading(false);
      showMessage(`Załadowano ${parsedData.products.length} produktów!`, 'success');
      
    } catch (error) {
      console.error('Błąd podczas parsowania XML:', error);
      setError(`Błąd: ${error.message}`);
      setLoading(false);
      showMessage(`Błąd: ${error.message}`, 'error');
    }
  };

  // Funkcja do obsługi wgrania pliku XML
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Czytamy zawartość pliku
      const xmlContent = await readFileAsText(file);
      
      // Parsujemy plik XML
      const parsedData = await parseXMLFile(xmlContent, true);
      
      // Sprawdzamy, czy mamy produkty
      if (!parsedData.products || parsedData.products.length === 0) {
        throw new Error('Nie znaleziono produktów w pliku XML');
      }
      
      console.log(`Załadowano ${parsedData.products.length} produktów z pliku`);
      
      // Aktualizujemy stan aplikacji
      setProducts(parsedData.products);
      setNodes(parsedData.menuNodes);
      
      // Automatycznie zapisujemy początkowy stan produktów po wczytaniu pliku XML
      const fileName = file.name || 'plik.xml';
      const shortFileName = fileName.length > 15 ? 
        fileName.substring(0, 12) + '...' : 
        fileName;
      
      // Zapisujemy początkowy stan
      saveCurrentPrioritiesSnapshot(parsedData.products, shortFileName);
      console.log('Zapisano początkowy stan produktów z pliku:', shortFileName);
      
      setLoading(false);
      setError(null);
      
    } catch (error) {
      console.error('Błąd podczas wczytywania pliku:', error);
      setError(`Błąd podczas wczytywania pliku: ${error.message}`);
      setLoading(false);
    }
  };

  // Funkcja przetwarzająca produkty do wyświetlenia - obsługuje produkty zgrupowane
  const processProductsForDisplay = useCallback((products) => {
    if (!products || products.length === 0) {
      console.warn("Brak produktów do przetworzenia");
      return [];
    }

    console.log(`Przetwarzanie ${products.length} produktów...`);
    
    // Najpierw tworzymy zbiór wszystkich ID produktów, które powinny być ukryte
    // z powodu bycia częścią grupy z trybem select_product lub first_product, ale nie są wybranymi produktami
    const productsToHide = new Set();
    
    // Mapa do śledzenia grup i ich produktów
    const groupMap = new Map();
    
    // Pierwsza iteracja - identyfikujemy grupy i mapujemy produkty do grup
    products.forEach(product => {
      if (!product.originalData || !product.originalData.group) return;
      
      const group = product.originalData.group;
      if (!group) return;
      
      // Poprawiona ścieżka do ustawień wyświetlania
      const displaySettings = group["iaiext:products_display_settings"];
      if (!displaySettings || !displaySettings["iaiext:show_in_store"]) return;
      
      const showInStore = displaySettings["iaiext:show_in_store"];
      
      // Sprawdzamy, czy grupa ma ustawienia wyświetlania typu select_product lub first_product
      if ((showInStore.value === 'select_product' || showInStore.value === 'first_product') && showInStore.product_id) {
        const groupId = group.id || `auto_group_${product.id}`;
        
        if (!groupMap.has(groupId)) {
          groupMap.set(groupId, {
            selectedProductId: showInStore.product_id,
            showInStoreValue: showInStore.value,
            products: []
          });
        }
        
        groupMap.get(groupId).products.push(product.id);
      }
    });
    
    // Teraz przetwarzamy zgromadzone informacje o grupach
    groupMap.forEach((groupInfo, groupId) => {
      const { selectedProductId, products: groupProducts, showInStoreValue } = groupInfo;
      
      if (showInStoreValue === 'select_product' || showInStoreValue === 'first_product') {
        console.log(`Grupa ${groupId} ma tryb ${showInStoreValue} z wybranym produktem ${selectedProductId}`);
        console.log(`Do grupy ${groupId} należy ${groupProducts.length} produktów`);
        
        // Dla obu trybów (select_product i first_product) ukrywamy wszystkie produkty oprócz wybranego product_id
        groupProducts.forEach(productId => {
          if (productId.toString() !== selectedProductId.toString()) {
            productsToHide.add(productId.toString());
            console.log(`Produkt ${productId} zostanie ukryty, bo nie jest wybranym produktem (${selectedProductId}) w grupie ${showInStoreValue}`);
          }
        });
      }
    });
    
    // Teraz filtrujemy produkty, usuwając te, które powinny być ukryte
    const filteredProducts = products.filter(product => {
      const shouldShow = !productsToHide.has(product.id.toString());
      if (!shouldShow) {
        console.log(`Ukrywam produkt ${product.id} zgodnie z regułami wyświetlania grup`);
      }
      return shouldShow;
    });
    
    console.log(`Po filtrowaniu grup pozostało ${filteredProducts.length} produktów`);
    return filteredProducts;
  }, []);

  const handleNodeSelect = useCallback((node) => {
    setSelectedNode(node);
    console.log(`Wybrano węzeł: ${node}`);
    
    // Różne możliwe formaty węzła - ze względu na różne sposoby zapisu ukośników
    const possibleNodeFormats = [
      node,
      node.replace(/\\/g, '\\\\'),       // Zamiana \ na \\
      node.replace(/\\\\/g, '\\'),       // Zamiana \\ na \
      node.replace(/\\/g, '/'),          // Zamiana \ na /
      node.replace(/\//g, '\\'),         // Zamiana / na \
      node.replace(/\\\\/g, '/')         // Zamiana \\ na /
    ];
    
    console.log('DEBUG: Sprawdzane formaty węzła:', possibleNodeFormats);
    
    // Wypisujemy wszystkie węzły menu dostępne w produktach
    const allMenuNodes = new Set();
    modifiedProducts.forEach(product => {
      product.menuItems.forEach(item => {
        allMenuNodes.add(item.textId);
      });
    });
    
    console.log('DEBUG: Wszystkie dostępne węzły menu w produktach:');
    [...allMenuNodes].sort().forEach(menuNode => {
      console.log(`- "${menuNode}"`);
    });
    
    // Debugowanie - sprawdzamy strukturę modifiedProducts
    console.log(`DEBUG: Liczba wszystkich produktów: ${modifiedProducts.length}`);
    
    // Bardziej zaawansowane porównywanie węzłów
    const nodeProducts = modifiedProducts.filter(product => 
      product.menuItems.some(item => {
        const itemTextId = item.textId || '';
        
        // Bezpośrednie porównanie
        const exactMatch = possibleNodeFormats.includes(itemTextId);
        if (exactMatch) {
          console.log(`DEBUG: Dokładne dopasowanie dla produktu ${product.id}: "${itemTextId}" === "${node}"`);
          return true;
        }
        
        // Sprawdzanie różnych formatów ścieżek
        const normalizedNodeFormats = possibleNodeFormats.map(f => f.toLowerCase().trim());
        const normalizedItemId = itemTextId.toLowerCase().trim();
        const normalizedMatch = normalizedNodeFormats.includes(normalizedItemId);
        
        if (normalizedMatch) {
          console.log(`DEBUG: Znormalizowane dopasowanie dla produktu ${product.id}: "${normalizedItemId}" w "${normalizedNodeFormats}"`);
          return true;
        }
        
        return false;
      })
    );
    
    console.log(`Znaleziono ${nodeProducts.length} produktów w węźle ${node}`);
    
    // Wypisujemy dokładne dane menu items każdego produktu
    console.log('DEBUG: Szczegółowa analiza menuItems produktów:');
    modifiedProducts.forEach(product => {
      const relevantMenuItems = product.menuItems.filter(item => {
        const itemTextId = item.textId || '';
        const match = possibleNodeFormats.includes(itemTextId) || 
                    possibleNodeFormats.map(f => f.toLowerCase().trim()).includes(itemTextId.toLowerCase().trim());
        
        if (match) {
          console.log(`DEBUG: Znaleziono dopasowanie! Produkt ${product.id}: "${itemTextId}" pasuje do węzła "${node}"`);
        }
        return match;
      });
      
      if (relevantMenuItems.length === 0) {
        // Szukamy podobnych węzłów dla diagnostyki - bardziej zaawansowane porównanie
        const similarItems = product.menuItems.filter(item => {
          const itemTextId = item.textId || '';
          return (
            itemTextId.includes('Marynark') || 
            itemTextId.toLowerCase().includes('marynark') ||
            node.includes(itemTextId) ||
            itemTextId.includes(node.replace(/\\/g, '')) ||
            // Porównanie bez uwzględniania wielkości liter
            itemTextId.toLowerCase().includes(node.toLowerCase()) ||
            node.toLowerCase().includes(itemTextId.toLowerCase()) ||
            // Porównanie tylko części ścieżki (ostatni segment)
            // eslint-disable-next-line no-useless-escape
            itemTextId.split(/[\\\/]/).pop().toLowerCase() === node.split(/[\\\/]/).pop().toLowerCase()
          );
        });
        
        if (similarItems.length > 0) {
          console.log(`DEBUG: Produkt ${product.id} ma podobne węzły:`);
          similarItems.forEach(item => {
            console.log(`- "${item.textId}" (level: ${item.level})`);
          });
        }
      }
    });
    
    if (nodeProducts.length > 0) {
      // Tworzymy kopię tablicy produktów z węzła
      const productsCopy = [...nodeProducts];
      
      // Sortujemy produkty według poziomu w menu
      productsCopy.sort((a, b) => {
        // Znajdujemy menu item dla każdego produktu, który pasuje do jednego z możliwych formatów
        const aMenuItem = a.menuItems.find(item => {
          const itemTextId = item.textId || '';
          return possibleNodeFormats.includes(itemTextId) || 
                 possibleNodeFormats.map(f => f.toLowerCase().trim()).includes(itemTextId.toLowerCase().trim());
        });
        
        const bMenuItem = b.menuItems.find(item => {
          const itemTextId = item.textId || '';
          return possibleNodeFormats.includes(itemTextId) || 
                 possibleNodeFormats.map(f => f.toLowerCase().trim()).includes(itemTextId.toLowerCase().trim());
        });
        
        // Sortujemy malejąco według wartości level (wyższy level = wyższa pozycja)
        if (aMenuItem && bMenuItem) {
          // Jeśli poziomy są różne, sortujemy według nich
          if (bMenuItem.level !== aMenuItem.level) {
            return bMenuItem.level - aMenuItem.level;
          }
          
          // Jeśli poziomy są identyczne, sortujemy według ID produktu (wyższe ID = wyższa pozycja)
          return parseInt(b.id) - parseInt(a.id);
        }
        
        // Jeśli jeden z produktów nie ma menuItem dla wybranego węzła, umieszczamy go na końcu
        if (aMenuItem) return -1;
        if (bMenuItem) return 1;
        
        return 0;
      });
      
      // Najpierw przetwarzamy posortowane produkty, aby odpowiednio obsłużyć grupy
      const processedProducts = processProductsForDisplay(productsCopy);
      console.log(`Po przetworzeniu grup: ${processedProducts.length} produktów`);
      
      setFilteredProducts(processedProducts);
    } else {
      // Debugowanie - bardziej zaawansowane wyszukiwanie podobnych węzłów
      console.log("DEBUG: Szukam podobnych węzłów:");
      const similarNodes = new Set();
      modifiedProducts.forEach(product => {
        product.menuItems.forEach(item => {
          const itemTextId = item.textId || '';
          if (itemTextId.includes('Marynark') || 
              itemTextId.toLowerCase().includes('marynark') ||
              node.includes(itemTextId) ||
              itemTextId.includes(node.replace(/\\/g, '')) ||
              // Porównanie bez uwzględniania wielkości liter
              itemTextId.toLowerCase().includes(node.toLowerCase()) ||
              node.toLowerCase().includes(itemTextId.toLowerCase()) ||
              // Porównanie tylko części ścieżki (ostatni segment)
              // eslint-disable-next-line no-useless-escape
              itemTextId.split(/[\\\/]/).pop().toLowerCase() === node.split(/[\\\/]/).pop().toLowerCase()) {
            similarNodes.add(itemTextId);
          }
        });
      });
      console.log("Podobne węzły:", [...similarNodes]);
      
      setFilteredProducts([]);
    }
  }, [modifiedProducts, processProductsForDisplay]);

  const handleProductsReorder = useCallback((newProducts) => {
    if (!selectedNode) return;

    console.log('Selected node:', selectedNode);
    console.log('Modified nodes before:', [...modifiedNodes]);

    setModifiedNodes(prev => {
      const newNodes = new Set([...prev, selectedNode]);
      console.log('Current node path:', selectedNode);
      
      if (selectedNode === 'SKLEP' || selectedNode === 'SKLEP\\Zobacz Wszystko') {
        const relatedNode = selectedNode === 'SKLEP' ? 'SKLEP\\Zobacz Wszystko' : 'SKLEP';
        newNodes.add(relatedNode);
      }
      if (selectedNode.endsWith('\\Kamizelki')) {
        console.log('Adding Kamizelki node');
        newNodes.add(selectedNode);
      }
      
      console.log('Modified nodes after:', [...newNodes]);
      return newNodes;
    });

    const startPriority = 930;
    
    setModifiedProducts(prevProducts => {
      const updatedProducts = [...prevProducts];
      
      newProducts.forEach((product, index) => {
        const productIndex = updatedProducts.findIndex(p => p.id === product.id);
        if (productIndex === -1) return;
        
        const newPriority = startPriority - index;
        
        updatedProducts[productIndex] = {
          ...updatedProducts[productIndex],
          menuItems: updatedProducts[productIndex].menuItems.map(item => {
            if (item.textId === selectedNode) {
              console.log(`Updating priority for ${product.id} in node ${selectedNode} to ${newPriority}`);
              return { ...item, level: newPriority };
            }
            if ((selectedNode === 'SKLEP' && item.textId === 'SKLEP\\Zobacz Wszystko') ||
                (selectedNode === 'SKLEP\\Zobacz Wszystko' && item.textId === 'SKLEP')) {
              return { ...item, level: newPriority };
            }
            return item;
          })
        };
      });

      return updatedProducts;
    });

    const updatedFilteredProducts = newProducts.map((product, index) => ({
      ...product,
      menuItems: product.menuItems.map(item => {
        if (item.textId === selectedNode) {
          return {
            ...item,
            level: startPriority - index
          };
        }
        if ((selectedNode === 'SKLEP' && item.textId === 'SKLEP\\Zobacz Wszystko') ||
            (selectedNode === 'SKLEP\\Zobacz Wszystko' && item.textId === 'SKLEP')) {
          return {
            ...item,
            level: startPriority - index
          };
        }
        return item;
      })
    }));

    setFilteredProducts(updatedFilteredProducts);
  }, [selectedNode, modifiedNodes]);

  const handleSaveToStore = async () => {
    try {
      if (!modifiedProducts.length) {
        throw new Error('Brak produktów do zapisania');
      }

      setIsSaving(true);
      console.log('=== ROZPOCZĘCIE PROCESU ZAPISU DO SKLEPU ===');

      // Zrzut stanu przed zapisem do debugowania
      console.log(`Stan przed zapisem: ${modifiedProducts.length} produktów`);
      console.log(`Zmodyfikowane węzły przed zapisem (${modifiedNodes.size}):`, [...modifiedNodes]);
      console.log(`Zapisane węzły przed zapisem (${savedNodes.size}):`, [...savedNodes]);

      // Filtrujemy produkty, które mają zmodyfikowane węzły
      const productsToUpdate = modifiedProducts.filter(product => 
        product.menuItems.some(item => modifiedNodes.has(item.textId))
      );

      if (productsToUpdate.length === 0) {
        throw new Error('Brak produktów ze zmodyfikowanymi węzłami');
      }

      console.log(`Produkty do aktualizacji: ${productsToUpdate.length}`);
      
      // Automatycznie zapisujemy migawkę przed wysłaniem zmian
      const timestamp = new Date().toLocaleString('pl-PL', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
      
      // Zamiast pełnych ścieżek węzłów, używamy tylko ich liczby
      const nodeCount = modifiedNodes.size;
      const snapshotName = `Zapis [${timestamp}] - ${nodeCount} węzłów`;
      
      // Używamy trybu 'system' aby nie nadpisać oryginalnego stanu początkowego
      saveCurrentPrioritiesSnapshot(modifiedProducts, snapshotName, 'system');
      console.log('Zapisano automatyczną migawkę przed wysłaniem:', snapshotName);

      // Wywołujemy API do aktualizacji priorytetów
      const result = await updateProductPriorities(productsToUpdate);
      console.log('Odpowiedź z API po aktualizacji:', result);

      // Zapisujemy aktualnie zmodyfikowane węzły do zbioru zapisanych węzłów
      const updatedSavedNodes = new Set(savedNodes);
      modifiedNodes.forEach(nodeId => {
        updatedSavedNodes.add(nodeId);
      });
      
      // Aktualizujemy zbiór zapisanych węzłów
      setSavedNodes(updatedSavedNodes);
      console.log('Zaktualizowano zapisane węzły:', [...updatedSavedNodes]);

      // Czyszczenie listy zmodyfikowanych węzłów po zapisie
      setModifiedNodes(new Set());
      console.log('Wyczyszczono zbiór zmodyfikowanych węzłów');
      
      // Wyświetlamy komunikat o pomyślnym zapisie
      showMessage('Zmiany zostały pomyślnie zapisane w sklepie!');
      console.log('=== ZAKOŃCZENIE PROCESU ZAPISU DO SKLEPU ===');
      setIsSaving(false);
    } catch (error) {
      console.error('Błąd podczas zapisywania:', error);
      setIsSaving(false);
      // Dodajemy więcej szczegółów do komunikatu błędu
      const errorMessage = error.message || 'Nieznany błąd';
      const errorDetails = error.stack ? `\nSzczegóły: ${error.stack}` : '';
      setError(`Błąd podczas zapisywania: ${errorMessage}${errorDetails}`);
    }
  };

  // Funkcja obsługująca przywrócenie stanu początkowego produktów
  const handleRestoreInitialState = useCallback((initialProducts) => {
    try {
      console.log('=== PRZYWRACANIE POCZĄTKOWEGO STANU PRODUKTÓW W APP.JS ===');
      
      if (!initialProducts || !Array.isArray(initialProducts)) {
        console.error('Nieprawidłowe dane początkowe:', initialProducts);
        return;
      }
      
      console.log(`Otrzymano ${initialProducts.length} produktów do przywrócenia`);
      console.log('Przykładowy produkt ID:', initialProducts[0].id);
      console.log('Przykładowy produkt name:', initialProducts[0].name);
      console.log('Przykładowy produkt ma zdjęcie:', !!initialProducts[0].iconUrl);
      
      // Głęboka kopia produktów, aby uniknąć problemów z referencjami
      const productsCopy = JSON.parse(JSON.stringify(initialProducts));
      console.log('Utworzono głęboką kopię produktów');
      
      // Aktualizujemy stan produktów
      setModifiedProducts(productsCopy);
      console.log('Zaktualizowano stan modifiedProducts');
      
      // Resetujemy zbiór zmodyfikowanych węzłów
      setModifiedNodes(new Set());
      console.log('Zresetowano zbiór modifiedNodes');
      
      // Jeśli jest wybrany węzeł, aktualizujemy wyświetlane produkty
      if (selectedNode) {
        console.log(`Odświeżanie widoku dla węzła: ${selectedNode}`);
        
        // Filtrujemy produkty dla bieżącego węzła
        const nodeProducts = productsCopy.filter(product => 
          product.menuItems && product.menuItems.some(item => 
            item.textId === selectedNode
          )
        );
        
        console.log(`Znaleziono ${nodeProducts.length} produktów dla węzła ${selectedNode}`);
        
        // Sortujemy produkty według poziomu
        const sortedProducts = [...nodeProducts].sort((a, b) => {
          const aMenuItem = a.menuItems.find(item => item.textId === selectedNode);
          const bMenuItem = b.menuItems.find(item => item.textId === selectedNode);
          
          if (aMenuItem && bMenuItem) {
            return bMenuItem.level - aMenuItem.level;
          }
          
          if (aMenuItem) return -1;
          if (bMenuItem) return 1;
          
          return 0;
        });
        
        // Przetwarzamy produkty, aby obsłużyć grupy
        const processedProducts = processProductsForDisplay(sortedProducts);
        console.log(`Po przetworzeniu zostało ${processedProducts.length} produktów`);
        
        // Sprawdzamy, czy produkty mają zdjęcia
        console.log('Pierwsze 3 produkty po przetworzeniu:');
        processedProducts.slice(0, 3).forEach((p, i) => {
          console.log(`Produkt ${i+1} ID:${p.id}, ma zdjęcie:`, !!p.iconUrl);
        });
        
        // Aktualizujemy wyświetlane produkty
        setFilteredProducts(processedProducts);
        console.log('Zaktualizowano stan filteredProducts');
      }
      
      console.log('Pomyślnie przywrócono początkowy stan produktów');
      console.log('=== ZAKOŃCZENIE PRZYWRACANIA STANU POCZĄTKOWEGO ===');
      
    } catch (error) {
      console.error('Błąd podczas przywracania początkowego stanu:', error);
      showMessage(`Wystąpił błąd podczas przywracania: ${error.message}`, 'error');
    }
  }, [selectedNode, processProductsForDisplay]);

  // Zapisywanie nowego klucza API IdoSell (z zakładki Ustawienia)
  const handleSaveApiKey = async () => {
    const key = settingsApiKey.trim();
    if (!key) {
      showMessage('Wpisz nowy klucz API', 'error');
      return;
    }
    try {
      setIsSavingSettings(true);
      await updateIdoSellApiKey(key);
      showMessage('Klucz API został zaktualizowany.', 'success');
      setSettingsApiKey('');
      setIsSettingsOpen(false);
    } catch (err) {
      showMessage(err.message || 'Nie udało się zapisać klucza.', 'error');
    } finally {
      setIsSavingSettings(false);
    }
  };

  // Funkcja do odświeżania widoczności produktów
  const handleRefreshProductsVisibility = async () => {
    if (!filteredProducts || filteredProducts.length === 0) {
      showMessage('Nie wybrano żadnych produktów do odświeżenia widoczności.', 'warning');
      return;
    }
    
    try {
      setIsRefreshingVisibility(true);
      setRefreshProgress({ current: 0, total: filteredProducts.length, percent: 0 });
      setRefreshMessage('Przygotowywanie do odświeżenia widoczności produktów...');
      
      // Przygotowanie listy identyfikatorów produktów
      const productIds = filteredProducts.map(product => parseInt(product.id));
      const totalProducts = productIds.length;
      const batchSize = 5;
      
      // ZMIANA: Zaimplementowanie logiki odświeżania widoczności po stronie klienta
      // wykorzystując istniejący endpoint /api/products/products
      console.log('Odświeżanie widoczności produktów po stronie klienta:', {
        productIds,
        batchSize: batchSize,
        delayBetweenHideShow: 2000,
        delayBetweenBatches: 1000
      });
      
      // Podział produktów na partie
      const batches = [];
      for (let i = 0; i < productIds.length; i += batchSize) {
        batches.push(productIds.slice(i, i + batchSize));
      }
      
      let processedCount = 0;
      
      // Funkcja do ukrywania produktów
      const hideProducts = async (ids) => {
        const response = await fetch(`${getKMXApiBase()}/api/products/products`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-API-KEY': API_KEY,
          },
          body: JSON.stringify({
            params: {
              products: ids.map(id => ({
                productInVisible: "n", // Ukryj produkt
                productId: id
              }))
            }
          }),
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error(`Błąd podczas ukrywania produktów: ${response.status} ${response.statusText}`);
        }
        
        return await response.json();
      };
      
      // Funkcja do pokazywania produktów
      const showProducts = async (ids) => {
        const response = await fetch(`${getKMXApiBase()}/api/products/products`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-API-KEY': API_KEY,
          },
          body: JSON.stringify({
            params: {
              products: ids.map(id => ({
                productInVisible: "y", // Pokaż produkt
                productId: id
              }))
            }
          }),
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error(`Błąd podczas pokazywania produktów: ${response.status} ${response.statusText}`);
        }
        
        return await response.json();
      };
      
      // Funkcja pomocnicza do opóźnienia
      const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
      
      setRefreshMessage(`Rozpoczęto proces odświeżania widoczności ${totalProducts} produktów w partiach po ${batchSize}.`);
      
      // Proces odświeżania widoczności
      (async () => {
        for (let i = 0; i < batches.length; i++) {
          const batch = batches[i];
          try {
            // 1. Ukryj produkty
            await hideProducts(batch);
            
            // 2. Czekaj
            await delay(2000); // 2 sekundy pomiędzy ukryciem a pokazaniem
            
            // 3. Pokaż produkty
            await showProducts(batch);
            
            // 4. Aktualizacja postępu
            processedCount += batch.length;
            setRefreshProgress({
              current: processedCount,
              total: totalProducts,
              percent: Math.round((processedCount / totalProducts) * 100)
            });
            
            // 5. Czekaj przed kolejną partią
            if (i < batches.length - 1) {
              await delay(1000); // 1 sekunda pomiędzy partiami
            }
          } catch (error) {
            console.error(`Błąd podczas przetwarzania partii ${i+1}:`, error);
            throw error; // Przekaż błąd dalej
          }
        }
        
        // Zakończenie procesu
        setRefreshMessage('Zakończono odświeżanie widoczności produktów!');
        
        // Zamknij modal po zakończeniu
        setTimeout(() => {
          setIsRefreshingVisibility(false);
          showMessage('Odświeżanie widoczności produktów zakończone pomyślnie!', 'success');
        }, 3000);
      })().catch(error => {
        console.error('Błąd podczas procesu odświeżania widoczności:', error);
        setRefreshMessage(`Błąd: ${error.message}`);
        
        // Zamknij modal po błędzie
        setTimeout(() => {
          setIsRefreshingVisibility(false);
          showMessage(`Błąd: ${error.message}`, 'error');
        }, 3000);
      });
      
    } catch (error) {
      console.error('Błąd podczas odświeżania widoczności produktów:', error);
      setRefreshMessage(`Błąd: ${error.message}`);
      
      // Zamknij modal po błędzie
      setTimeout(() => {
        setIsRefreshingVisibility(false);
        showMessage(`Błąd: ${error.message}`, 'error');
      }, 3000);
    }
  };

  useEffect(() => {
    // Automatycznie preloaduj XML z serwera przy starcie
    preloadXMLFromServer();
  }, [preloadXMLFromServer]);

  // Sprawdzaj nowy plik XML co 5 minut
  useEffect(() => {
    const interval = setInterval(() => {
      if (preloadedXML) { // Tylko jeśli już mamy pobrany plik
        checkForNewXML();
      }
    }, 5 * 60 * 1000); // Co 5 minut
    
    return () => clearInterval(interval);
  }, [preloadedXML, checkForNewXML]);

  // Sprawdzaj nowy plik przy odświeżeniu strony (focus na oknie)
  useEffect(() => {
    const handleFocus = () => {
      if (preloadedXML) {
        checkForNewXML();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [preloadedXML, checkForNewXML]);

  // Dodajemy efekt, który aktualizuje modifiedProducts po wczytaniu pliku
  useEffect(() => {
    if (products && products.length > 0) {
      console.log(`Aktualizacja modifiedProducts na podstawie wczytanych produktów: ${products.length}`);
      setModifiedProducts(products);
    }
  }, [products]);

  // Dodajemy efekt, który reaguje na zmiany w modifiedProducts i odświeża wyświetlane produkty
  useEffect(() => {
    if (selectedNode && modifiedProducts.length > 0) {
      console.log(`Odświeżanie widoku dla węzła ${selectedNode} po zmianie modifiedProducts`);
      
      // Różne możliwe formaty węzła
      const possibleNodeFormats = [
        selectedNode,
        selectedNode.replace(/\\/g, '\\\\'),
        selectedNode.replace(/\\\\/g, '\\'),
        selectedNode.replace(/\\/g, '/'),
        selectedNode.replace(/\//g, '\\'),
        selectedNode.replace(/\\\\/g, '/')
      ];
      
      // Filtrujemy i sortujemy produkty zgodnie z logiką handleNodeSelect
      const nodeProducts = modifiedProducts.filter(product => 
        product.menuItems && product.menuItems.some(item => {
          const itemTextId = item.textId || '';
          
          return possibleNodeFormats.includes(itemTextId) || 
                 possibleNodeFormats.map(f => f.toLowerCase().trim()).includes(itemTextId.toLowerCase().trim());
        })
      );
      
      // Sortujemy znalezione produkty według poziomu (priority/level)
      const sortedProducts = [...nodeProducts].sort((a, b) => {
        const aMenuItem = a.menuItems.find(item => {
          const itemTextId = item.textId || '';
          return possibleNodeFormats.includes(itemTextId) || 
                 possibleNodeFormats.map(f => f.toLowerCase().trim()).includes(itemTextId.toLowerCase().trim());
        });
        
        const bMenuItem = b.menuItems.find(item => {
          const itemTextId = item.textId || '';
          return possibleNodeFormats.includes(itemTextId) || 
                 possibleNodeFormats.map(f => f.toLowerCase().trim()).includes(itemTextId.toLowerCase().trim());
        });
        
        if (aMenuItem && bMenuItem) {
          if (bMenuItem.level !== aMenuItem.level) {
            return bMenuItem.level - aMenuItem.level;
          }
          return parseInt(b.id) - parseInt(a.id);
        }
        
        if (aMenuItem) return -1;
        if (bMenuItem) return 1;
        
        return 0;
      });
      
      // Przetwarzamy produkty, aby obsłużyć grupy
      const processed = processProductsForDisplay(sortedProducts);
      console.log(`Znaleziono ${sortedProducts.length} produktów dla węzła ${selectedNode}, po przetworzeniu: ${processed.length}`);
      
      // Aktualizujemy wyświetlane produkty
      setFilteredProducts(processed);
    }
  }, [modifiedProducts, selectedNode, processProductsForDisplay]);

  // Usunięto: stary LoadingSpinner zastąpiony przez modal z postępem

  if (error) {
    return (
      <div className="app">
        <header className="app-header">
          <h1>SortujTo! - Zarządzanie priorytetami produktów</h1>
        </header>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="app-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
        <h1>SortujTo! - Zarządzanie priorytetami produktów</h1>
        <button
          type="button"
          onClick={() => setIsSettingsOpen(true)}
          title="Ustawienia (klucz API)"
          style={{
            background: 'transparent',
            border: 'none',
            color: 'rgba(255,255,255,0.5)',
            padding: '6px 8px',
            cursor: 'pointer',
            fontSize: '18px',
            lineHeight: 1,
          }}
          aria-label="Ustawienia"
        >
          ⚙️
        </button>
      </header>
      
      {/* Komunikaty dla użytkownika */}
      {message && (
        <div className={`alert alert-${messageType}`}>
          {message}
          <button onClick={() => setMessage('')} className="close-button">×</button>
        </div>
      )}
      
      {!products ? (
        <div className="app-content">
          <InitialMessage>
            <h2 style={{ marginBottom: '8px', color: '#333', fontSize: '20px', fontWeight: '500' }}>
              Wczytaj ofertę produktów
            </h2>
            
            <LoadOptionsContainer>
              {/* LEWA SEKCJA - Pobierz z serwera */}
              <LoadOption>
                <OptionTitle>🌐 Oferta z serwera</OptionTitle>
                
                {/* Informacja o dacie generowania */}
                
                {isPreloading ? (
                  // Pokazuj pasek postępu podczas pobierania
                  <>
                    <OptionDescription>
                      Pobieranie aktualnej oferty...
                    </OptionDescription>
                    
                    <div style={{ width: '100%', marginTop: '20px' }}>
                      <div className="progress-container" style={{ margin: '0' }}>
                        <div 
                          className="progress-bar" 
                          style={{ width: `${downloadProgress.percent}%` }}
                        ></div>
                      </div>
                      
                      {downloadProgress.total > 0 ? (
                        <div style={{ 
                          marginTop: '10px', 
                          fontSize: '12px', 
                          color: '#666',
                          textAlign: 'center'
                        }}>
                          {(downloadProgress.loaded / 1024 / 1024).toFixed(2)} MB / {(downloadProgress.total / 1024 / 1024).toFixed(2)} MB ({downloadProgress.percent}%)
                        </div>
                      ) : (
                        <div style={{ 
                          marginTop: '10px', 
                          fontSize: '12px', 
                          color: '#666',
                          textAlign: 'center'
                        }}>
                          Łączenie z serwerem...
                        </div>
                      )}
                    </div>
                  </>
                ) : preloadedXML ? (
                  // Pokazuj informacje o pobranej ofercie w jednej ramce
                  <>
                    {/* Połączone informacje o dacie i liczniku */}
                    <div style={{
                      marginTop: '12px',
                      padding: '16px',
                      background: '#fafafa',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '12px',
                      color: '#666',
                      width: '100%',
                      minHeight: '80px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center'
                    }}>
                      {offerGeneratedDate && (
                        <div style={{ marginBottom: '12px', textAlign: 'center' }}>
                          📅 Wygenerowano: {new Date(offerGeneratedDate).toLocaleString('pl-PL', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      )}
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ marginBottom: '4px' }}>
                          ⏱️ Następna aktualizacja za:
                        </div>
                        <div style={{ fontSize: '15px', fontWeight: '600', color: '#0EB66E' }}>
                          {timeToUpdate.hours}h {timeToUpdate.minutes}min
                        </div>
                      </div>
                    </div>
                    
                    {/* Przycisk Pokaż produkty */}
                    <ActionButton 
                      variant="primary" 
                      onClick={handleLoadPreloadedXML}
                      fullWidth={true}
                      disabled={loading}
                      style={{ marginTop: '16px' }}
                    >
                      {loading ? 'Ładowanie...' : 'Pokaż produkty'}
                    </ActionButton>
                  </>
                ) : error ? (
                  // Pokazuj błąd i przycisk ponów
                  <>
                    <OptionDescription style={{ color: '#d9534f' }}>
                      Błąd podczas pobierania
                    </OptionDescription>
                    
                    <ActionButton 
                      variant="primary" 
                      onClick={preloadXMLFromServer}
                      fullWidth={false}
                    >
                      Ponów pobieranie
                    </ActionButton>
                  </>
                ) : null}
              </LoadOption>
              
              {/* PRAWA SEKCJA - Wgraj plik lokalny */}
              <LoadOption>
                <OptionTitle>💾 Wgraj plik lokalny</OptionTitle>
                
                {/* Połączone informacje w jednej ramce */}
                <div style={{
                  marginTop: '12px',
                  padding: '16px',
                  background: '#fafafa',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '12px',
                  color: '#666',
                  width: '100%',
                  minHeight: '80px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}>
                  <div style={{ marginBottom: '12px', textAlign: 'center' }}>
                    💾 Plik XML zapisany na dysku
                  </div>
                  <div style={{ textAlign: 'center', fontSize: '11px', color: '#888' }}>
                    Użyj tej opcji dla zapisanych kopii zapasowych
                  </div>
                </div>
                
                {/* Przycisk Wybierz plik XML */}
                <ActionButton 
                  variant="secondary" 
                  onClick={() => fileInputRef.current?.click()}
                  fullWidth={true}
                  disabled={loading}
                  style={{ marginTop: '16px' }}
                >
                  Wybierz plik XML
                </ActionButton>
                <FileInput 
                  type="file" 
                  accept=".xml" 
                  ref={fileInputRef} 
                  onChange={handleFileUpload} 
                />
              </LoadOption>
            </LoadOptionsContainer>
          </InitialMessage>
        </div>
      ) : (
        <AppContainer>
          <SidebarContainer>
            <MenuSelector 
              nodes={nodes} 
              activeNode={selectedNode} 
              onSelect={handleNodeSelect}
              savedNodes={savedNodes}
            />

            {/* Przyciski akcji */}
            <div className="action-buttons">
              <button 
                onClick={handleSaveToStore}
                disabled={loading || isSaving || modifiedNodes.size === 0 || isRefreshingVisibility}
                className="save-button"
              >
                {isSaving ? 'Wysyłanie...' : 'Zapisz zmiany w sklepie'}
              </button>
              
              {/* Przycisk do odświeżania widoczności produktów */}
              {filteredProducts && filteredProducts.length > 0 && (
                <button 
                  onClick={handleRefreshProductsVisibility}
                  disabled={loading || isSaving || isRefreshingVisibility}
                  className="refresh-visibility-button"
                >
                  {isRefreshingVisibility ? 'Odświeżanie...' : 'Odśwież widoczność produktów na listach'}
                </button>
              )}
              
              <div className="spacer"></div>
              
              <PrioritySnapshotsManager 
                onRestoreSnapshot={handleRestoreInitialState}
                isLoading={loading}
              />
            </div>
          </SidebarContainer>
          
          <MainContent>
            {selectedNode ? (
              <ProductGrid 
                products={filteredProducts} 
                onProductsReorder={handleProductsReorder}
                selectedNode={selectedNode}
              />
            ) : (
              <InitialMessage>
                Wybierz węzeł menu, aby zobaczyć produkty
              </InitialMessage>
            )}
          </MainContent>
        </AppContainer>
      )}
      
      <footer className="app-footer">
        <p>&copy; 2024 - {new Date().getFullYear()} SortujTo!</p>
      </footer>
      
      {/* Modal postępu odświeżania widoczności */}
      {isRefreshingVisibility && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Odświeżanie widoczności produktów</h3>
            
            <div className="progress-container">
              <div 
                className="progress-bar" 
                style={{ width: `${refreshProgress.percent}%` }}
              ></div>
            </div>
            
            <div className="progress-text">
              {refreshProgress.current} z {refreshProgress.total} ({refreshProgress.percent}%)
            </div>
            
            <p>{refreshMessage}</p>
            
            <p className="warning-text" style={{ color: '#d9534f', fontWeight: 'bold', margin: '15px 0' }}>
              Uwaga: Nie zamykaj okna przeglądarki przed zakończeniem procesu!
            </p>
          </div>
        </div>
      )}
      
      {/* Modal Ustawienia – aktualizacja klucza API IdoSell */}
      {isSettingsOpen && (
        <div className="modal-overlay" onClick={() => !isSavingSettings && setIsSettingsOpen(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '440px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}>Ustawienia</h3>
              <button
                type="button"
                onClick={() => !isSavingSettings && (setIsSettingsOpen(false), setSettingsApiKey(''))}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#666', lineHeight: 1 }}
                aria-label="Zamknij"
              >
                ×
              </button>
            </div>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px', lineHeight: 1.5 }}>
              Klucz API IdoSell jest używany do zapisywania zmian w sklepie. Zaktualizuj go tutaj, gdy wygaśnie (bez edycji kodu).
            </p>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
              Nowy klucz API IdoSell
            </label>
            <input
              type="password"
              value={settingsApiKey}
              onChange={e => setSettingsApiKey(e.target.value)}
              placeholder="Wklej nowy klucz API"
              autoComplete="off"
              style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '8px', marginBottom: '16px', boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => !isSavingSettings && (setIsSettingsOpen(false), setSettingsApiKey(''))}
                style={{ padding: '10px 18px', background: '#eee', border: '1px solid #ccc', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}
              >
                Anuluj
              </button>
              <button
                type="button"
                onClick={handleSaveApiKey}
                disabled={!settingsApiKey.trim() || isSavingSettings}
                style={{ padding: '10px 18px', background: settingsApiKey.trim() && !isSavingSettings ? '#0EB66E' : '#ccc', color: '#fff', border: 'none', borderRadius: '8px', cursor: settingsApiKey.trim() && !isSavingSettings ? 'pointer' : 'not-allowed', fontSize: '14px', fontWeight: '600' }}
              >
                {isSavingSettings ? 'Zapisywanie...' : 'Zapisz klucz'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal postępu pobierania XML - usunięty, używamy inline progress bar */}
    </div>
  );
}

export default App;


